import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-zebcast-purple to-zebcast-dark px-4 py-12">
      <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-2xl p-8 md:p-12">
        <div className="text-center mb-8">
          <Link to="/" className="text-4xl font-bold text-gray-900 hover:text-zebcast-purple transition-colors">
            Zebcast
          </Link>
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
        <p className="text-gray-500 mb-8">Last updated: 1 June 2026</p>

        <div className="prose prose-gray max-w-none space-y-6 text-gray-700 leading-relaxed">

          <p>
            EzBox Media Limited ("we", "our", or "us") operates the ZebCast application
            (package: com.ezbm.cast.zebcast) and the zebcast.com website. We are a company
            registered in the United Kingdom.
          </p>
          <p>
            This Privacy Policy explains what data we collect, why we collect it, and how
            you can control it. We believe in transparency — this document is designed to be
            read, not buried.
          </p>

          {/* ============================== */}
          <section>
            <div className="bg-zebcast-purple/10 border-l-4 border-zebcast-purple p-5 rounded-r-lg my-6">
              <h2 className="text-lg font-bold text-gray-900 mb-2">At a glance</h2>
              <ul className="list-disc pl-6 space-y-1 text-gray-800">
                <li><strong>We do not collect personal information.</strong> No name, email, phone
                    number, postal address, Google account, Apple ID, or advertising ID is collected
                    by the ZebCast app.</li>
                <li><strong>ZebCast itself does not set or read any cookies.</strong> The app has no
                    cookie store of its own.</li>
                <li>The embedded browser (Chromium WebView) handles cookies on behalf of the
                    websites <em>you</em> choose to visit (YouTube, Pluto, etc.) — those cookies
                    belong to those sites under their own privacy policies, exactly as if you
                    visited them in any other browser. They stay on your device. We do not read,
                    transmit, or share them.</li>
                <li>Advertisements served by Google AdMob (free tier only) may use Google's own
                    cookies / identifiers under Google's User Messaging Platform consent flow —
                    see Section 4.</li>
                <li>What we <em>do</em> collect (only with your consent on first launch and only
                    when you opt in) is described in detail in Section 1 below.</li>
              </ul>
            </div>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              1. What We Collect
            </h2>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.1 Device Information (collected once)</h3>
            <p>
              When you first open ZebCast, we collect basic information about your phone:
              manufacturer, model, Android version, available RAM, screen resolution, CPU and
              GPU model, and supported media capabilities. We also generate a random identifier
              (UUID) that is not linked to your Google account, phone number, or advertising ID.
              This identifier groups your reports together so we can track issues across sessions
              on the same device.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.2 Casting Session Data</h3>
            <p>When you cast media to a TV or device, we record:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The casting protocol used (DLNA, Chromecast, Miracast, or ZebCast Direct)</li>
              <li>The type of media (video, audio, or image — not the file name, URL, or content itself)</li>
              <li>The video and audio format and resolution</li>
              <li>The type of device you cast to (manufacturer, model, and chipset vendor of the TV or dongle)</li>
              <li>Whether the cast succeeded or failed, and what error occurred</li>
              <li>Session duration</li>
              <li>WiFi signal quality during the session</li>
              <li>Battery level at the start and end of the session</li>
              <li>Available phone memory</li>
              <li>
                The manufacturer prefix (OUI) of the receiving device's network address — the
                first three bytes of its MAC address. This identifies only the chipset or device
                manufacturer (for example, "Roku" or "Samsung") and is shared by millions of
                devices worldwide. It cannot identify your specific TV or dongle.
              </li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.3 App Usage Data</h3>
            <p>
              We record which features you use (such as browser casting, IPTV, or cloud file
              casting), which settings you change, and when you open the app.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.4 Crash Reports</h3>
            <p>
              If ZebCast crashes, Google's Firebase Crashlytics SDK collects the
              technical details of the crash and sends them to Google Firebase
              so we can find and fix the bug. The data sent includes:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The stack trace and the line of code that crashed.</li>
              <li>Device fingerprint: manufacturer, model, OS version, available
                  memory and disk space at the time of the crash.</li>
              <li>The app version and which Activity/Fragment was in the
                  foreground.</li>
              <li>A random session ID that lets us link multiple events from the
                  same launch — not linked to your device identifier from §1.1.</li>
            </ul>
            <p>
              Crash reports do NOT include the names or contents of files you
              were casting, URLs you visited, personal text you typed (e.g.
              Helper Genie messages), or audio. Crashlytics is implemented as
              a <strong>single-module integration</strong> — we do not bundle
              Firebase Analytics, Firebase Auth, Firebase Database, or any
              other Firebase service.
            </p>
            <p>
              Crash reporting initializes <strong>after the first frame has
              drawn</strong>, so it never slows down app startup. The SDK does
              not initialize at all unless you have opted in (see Section 5.2).
              In the EU, UK, and Switzerland, crash reporting is{' '}
              <strong>opt-in (default OFF)</strong>; elsewhere it is{' '}
              <strong>opt-out (default ON)</strong>, in line with each
              jurisdiction's expectations.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.5 Website Account Information</h3>
            <p>
              If you create an account for the analytics dashboard on zebcast.com, we collect
              your email address and encrypted password. Server logs may include your IP address,
              browser type, and pages visited.
            </p>
            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.6 Helper Genie (in-app chat assistant)</h3>
            <p>
              The Helper Genie is an in-app chat assistant that answers questions about ZebCast.
              When you open Helper Genie and type a message:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The text of your message is sent to our backend.</li>
              <li>Our backend forwards your message to <strong>Anthropic PBC's Claude API</strong>
                  (a sub-processor) to generate a reply. Anthropic processes your message under
                  its <a href="https://www.anthropic.com/legal/privacy"
                  target="_blank" rel="noopener noreferrer"
                  className="text-zebcast-purple hover:underline">Commercial Terms and Privacy
                  Policy</a>; per those terms, your input is not used to train Anthropic's
                  models when accessed via the commercial API.</li>
              <li>The text of every message you send and every reply you receive is stored on
                  our server in the <code className="text-sm bg-gray-100 px-1 rounded">chat_messages</code>
                  table along with your per-device identifier and a session ID. This lets us
                  improve answers and investigate quality issues.</li>
              <li>Helper Genie messages are retained for <strong>30 days</strong>, then
                  automatically deleted from our database.</li>
            </ul>
            <p>
              Helper Genie is opt-in. The first time you tap the Genie icon, you will be asked
              for consent before any text is sent. You can revoke consent at any time in
              Settings &rarr; Privacy &rarr; Helper Genie. Revoking consent disables the
              feature; messages already in our retention window will still be auto-deleted at
              the 30-day mark unless you submit a deletion request (see Section 5.4).
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.7 Voice Feedback (optional)</h3>
            <p>
              ZebCast's feedback widget includes an optional <strong>voice memo</strong> button
              that lets you record up to 60 seconds of audio describing a problem. Voice
              recordings constitute biometric data under the UK GDPR (Article 9 special-category
              data) and are handled accordingly:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Voice recording is <strong>off by default</strong>. You must explicitly tap
                  the microphone button each time you want to record — there is no continuous
                  or background recording.</li>
              <li>Recordings are uploaded only when you tap "Send" on the feedback form. You
                  can review, re-record, or discard before sending.</li>
              <li>Audio is sent to our backend and stored as an m4a file. We do not perform
                  speaker identification or any biometric analysis on the audio. We do not share
                  it with any third party. (We may in the future use a speech-to-text service to
                  read the audio as text for triage; if and when we do, this policy will be
                  updated and you will be asked to consent again.)</li>
              <li>Voice memos are retained for <strong>30 days</strong>, then automatically
                  deleted.</li>
              <li>You can request immediate deletion of all voice recordings associated with
                  your device by emailing privacy@zebcast.com (see Section 5.4).</li>
            </ul>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.8 Receiver-side Error Reports</h3>
            <p>
              When you cast to a Chromecast and our custom receiver code encounters a media
              error (decoder rejection, manifest parse failure, network timeout), the receiver
              page running on the Chromecast device sends a small report to our backend
              containing:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The error code and category (e.g. <code className="text-sm bg-gray-100 px-1 rounded">ERROR_CODE_104</code>,
                  <code className="text-sm bg-gray-100 px-1 rounded">hls.js parse</code>).</li>
              <li>The cast session ID and your device identifier (so we can correlate with the
                  sender-side relay events).</li>
              <li>The playback position at the time of error.</li>
              <li>The path component of the failed media URL with any signed-URL query string
                  parameters (auth tokens, JWTs) <strong>scrubbed</strong>.</li>
            </ul>
            <p>
              These reports are essential to identifying why casting fails on specific TV
              hardware or specific source sites. Receiver error reports follow the same opt-in
              telemetry setting (Section 5.1) — if you have disabled usage data, the receiver
              page is told not to report.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">1.9 DMS Live-TV IPTV Proxy</h3>
            <p>
              When you add an IPTV playlist (M3U file) and stream a channel from it to a TV via
              ZebCast's built-in DMS (DLNA media server), the phone proxies the upstream HLS
              segments so the TV's stock player can fetch them with a desktop User-Agent. During
              this proxy operation:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>The upstream URL is held in memory only for the duration of the streaming
                  session.</li>
              <li>We do not persist URLs, tokens, or playlist contents to our servers as part of
                  this feature.</li>
              <li>Phone-local debug logs may contain URLs with subscription credentials. If you
                  share a debug log with us for support, please redact tokens or check the
                  "Scrub credentials before sending" box in the feedback dialog (which strips
                  query strings from any URL longer than 256 characters).</li>
            </ul>

          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              2. What We Do NOT Collect
            </h2>
            <div className="bg-gray-50 border-l-4 border-zebcast-purple p-4 rounded-r-lg">
              <p>
                We do not collect the names or contents of files you cast. We do not collect URLs
                you visit in the ZebCast browser. We do not record your browsing history. We do
                not collect your real name, email address, phone number, Google account, or
                advertising ID. We do not collect the full MAC address or IP address of your TV,
                dongle, or phone — we store only a one-way hash of these values (which cannot be
                reversed to recover the original) solely to count unique devices. We do not record
                audio or video content from your screen. <strong>ZebCast itself does not set or
                read any cookies on your device</strong> — cookies set by the websites you visit
                (in the embedded browser) and by Google AdMob ads (free tier only) are governed
                by their own privacy policies; we do not access or aggregate them.
              </p>
            </div>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              3. Why We Collect This Data
            </h2>
            <p>
              ZebCast supports dozens of TV brands, streaming sticks, and dongles across multiple
              casting protocols. Each combination of phone, TV, and media format can behave
              differently. This data lets us:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Identify which device and format combinations fail, and prioritise fixes for the most common problems</li>
              <li>Build and maintain a device compatibility database so users know what works before they cast</li>
              <li>Understand which features matter most to users</li>
              <li>Detect performance regressions before they affect many people</li>
            </ul>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              4. Advertising
            </h2>
            <p>
              The free tier of ZebCast displays advertisements served by Google AdMob. AdMob may
              collect and use data to serve ads, including personalised ads where you have given
              consent. AdMob operates under its own data collection policy, which is separate from
              and independent of ZebCast's telemetry.
            </p>
            <p>
              Before showing personalised ads, ZebCast displays a consent dialog (powered by
              Google's User Messaging Platform) that lets you choose:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Accept personalised ads</li>
              <li>Accept non-personalised ads only</li>
              <li>Decline ads consent</li>
            </ul>
            <p>
              You can change your ad preferences at any time in Settings. If you subscribe to
              ZebCast Pro or ZebCast Plus, ads are removed entirely.
            </p>
            <p>
              For more information about how Google uses data in ads, see{' '}
              <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">
                Google's advertising policies
              </a>.
            </p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              5. Your Choices
            </h2>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">5.1 Telemetry — Opt In / Opt Out</h3>
            <p>
              On first launch, ZebCast asks you whether to enable telemetry. The default depends
              on your region: <strong>opt-in (default OFF) in the EU, UK, and Switzerland</strong>;
              <strong>opt-out (default ON) elsewhere</strong>, in keeping with each jurisdiction's
              requirements. You can change this at any time in Settings &rarr; Privacy &rarr;
              Usage Data. When disabled, no session or usage data is collected or transmitted,
              existing unsent records are purged from your device, and the receiver-side error
              reports described in Section 1.8 are also suppressed. The app functions normally
              with telemetry disabled.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">5.2 Crash Reports — Opt In / Opt Out</h3>
            <p>
              Crash reporting (Firebase Crashlytics) has its own toggle in the
              consent screen on first launch and in Settings &rarr; Privacy
              &rarr; Crash reports. You can change it independently of usage
              data. When disabled, the Crashlytics SDK is <strong>not initialized
              at all</strong> — no listener is registered, no data leaves your
              device. If you previously had it enabled and turn it off, we also
              instruct the SDK to discard any reports queued for upload.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">5.3 View Your Data</h3>
            <p>
              In Settings &rarr; Privacy &rarr; Your Data, you can see a summary of how many
              sessions have been reported and when the last report was sent. Tapping this shows
              the last 10 session reports in a readable format.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">5.4 Delete Your Data</h3>
            <p>
              You can request deletion of all data associated with your device by emailing{' '}
              <a href="mailto:privacy@zebcast.com" className="text-zebcast-purple hover:underline">
                privacy@zebcast.com
              </a>{' '}
              with the device identifier shown in Settings &rarr; Privacy &rarr; Your Device ID.
              We will process deletion requests within 30 days.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">5.5 Ad Preferences</h3>
            <p>
              You can change your ad consent preferences at any time in Settings &rarr;
              Privacy &rarr; Ad Preferences.
            </p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              6. Cloud File Access
            </h2>
            <p>
              ZebCast can connect to your cloud storage accounts to let you cast files stored
              there. When you connect a cloud service, ZebCast requests read-only access to
              browse and stream your files. We do not upload, modify, or delete any files in
              your cloud storage.
            </p>

            <div className="overflow-x-auto mt-4">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 border border-gray-200 font-semibold">Service</th>
                    <th className="text-left p-3 border border-gray-200 font-semibold">Access Requested</th>
                    <th className="text-left p-3 border border-gray-200 font-semibold">Authentication</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border border-gray-200">Google Drive</td>
                    <td className="p-3 border border-gray-200">Read-only file browsing and streaming</td>
                    <td className="p-3 border border-gray-200">Google OAuth 2.0</td>
                  </tr>
                  <tr>
                    <td className="p-3 border border-gray-200">Microsoft OneDrive</td>
                    <td className="p-3 border border-gray-200">Read-only file browsing and streaming</td>
                    <td className="p-3 border border-gray-200">Microsoft MSAL</td>
                  </tr>
                  <tr>
                    <td className="p-3 border border-gray-200">Dropbox</td>
                    <td className="p-3 border border-gray-200">Read-only file browsing and streaming</td>
                    <td className="p-3 border border-gray-200">Dropbox OAuth 2.0</td>
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="mt-4">
              Authentication tokens are stored securely on your device only. We do not store your
              cloud service credentials on our servers. You can disconnect any cloud service at
              any time in Settings, which revokes ZebCast's access. We do not collect or log the
              names, contents, or URLs of files you access through these services.
            </p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              7. Third-Party Services
            </h2>
            <p>ZebCast uses the following third-party services, each of which operates under its own privacy policy:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>
                <span className="font-medium">Google Firebase Crashlytics</span> (Google) —
                receives crash reports IF you have opted in (see Section 5.2).
                We use only the <code className="text-sm bg-gray-100 px-1 rounded">firebase-crashlytics</code>
                module — no Firebase Analytics, Auth, Database, or Storage.
                Subject to{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">Google's Privacy Policy</a>.
              </li>
              <li>
                <span className="font-medium">Google AdMob</span> — serves advertisements in the free tier. Subject to{' '}
                <a href="https://policies.google.com/technologies/ads" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">Google's advertising policies</a>.
              </li>
              <li>
                <span className="font-medium">Google Cast SDK</span> — enables Chromecast functionality. Subject to{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">Google's Privacy Policy</a>.
              </li>
              <li>
                <span className="font-medium">Google Drive API, Microsoft Graph API (OneDrive), Dropbox API</span> —
                enable cloud file access as described in Section 6.
              </li>
              <li>
                <span className="font-medium">Google reCAPTCHA</span> — for login security on zebcast.com. Subject to{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">Google's Privacy Policy</a> and Terms of Service.
              </li>
              <li>
                <span className="font-medium">Amazon Web Services</span> — hosting infrastructure. Subject to the{' '}
                <a href="https://aws.amazon.com/privacy/" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">AWS Privacy Policy</a>.
              </li>
            </ul>
            <p>We do not sell or share your data with any other third parties.</p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              8. Data Storage and Security
            </h2>
            <p>
              All data is transmitted over HTTPS (encrypted in transit) and stored on secured
              servers. Data is associated only with the random device identifier — not your identity.
              We implement industry-standard security measures including encryption in transit
              (HTTPS/TLS), secure authentication with JWT tokens, and encrypted password storage.
            </p>

            <h3 className="text-lg font-medium text-gray-800 mt-5 mb-2">Data Retention</h3>
            <div className="overflow-x-auto mt-2">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="text-left p-3 border border-gray-200 font-semibold">Data Type</th>
                    <th className="text-left p-3 border border-gray-200 font-semibold">Retention Period</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td className="p-3 border border-gray-200">Raw event and session data</td>
                    <td className="p-3 border border-gray-200">90 days</td>
                  </tr>
                  <tr>
                    <td className="p-3 border border-gray-200">Aggregated session data</td>
                    <td className="p-3 border border-gray-200">1 year</td>
                  </tr>
                  <tr>
                    <td className="p-3 border border-gray-200">Summarised device compatibility data</td>
                    <td className="p-3 border border-gray-200">Indefinitely (anonymised)</td>
                  </tr>
                  <tr>
                    <td className="p-3 border border-gray-200">Crash reports (Firebase Crashlytics)</td>
                    <td className="p-3 border border-gray-200">90 days (managed by Firebase)</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              9. Lawful Basis for Processing (UK GDPR)
            </h2>
            <p>We process data under the following lawful bases:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>
                <span className="font-medium">Legitimate interest</span> (Article 6(1)(f) UK GDPR) — for
                telemetry data that does not constitute personal data (no name, email, IP address,
                advertising ID, or data that can identify a natural person is collected). Our
                legitimate interest is improving app stability and device compatibility.
              </li>
              <li>
                <span className="font-medium">Consent</span> (Article 6(1)(a) UK GDPR) — for personalised
                advertising via AdMob. Consent is collected through the UMP consent dialog and can
                be withdrawn at any time.
              </li>
              <li>
                <span className="font-medium">Contract</span> (Article 6(1)(b) UK GDPR) — for data processing
                necessary to provide the casting service you have requested (e.g., connecting to
                your cloud storage accounts to stream files you select).
              </li>
            </ul>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              10. Your Rights Under UK GDPR
            </h2>
            <p>Under the UK General Data Protection Regulation, you have the right to:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><span className="font-medium">Access</span> — request a copy of the data we hold about you</li>
              <li><span className="font-medium">Rectification</span> — request correction of inaccurate data</li>
              <li><span className="font-medium">Erasure</span> — request deletion of your data</li>
              <li><span className="font-medium">Restriction</span> — request that we limit how we process your data</li>
              <li><span className="font-medium">Data portability</span> — request your data in a structured, machine-readable format</li>
              <li><span className="font-medium">Object</span> — object to processing based on legitimate interest</li>
              <li><span className="font-medium">Withdraw consent</span> — withdraw consent for personalised advertising at any time, without affecting the lawfulness of processing before withdrawal</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email{' '}
              <a href="mailto:privacy@zebcast.com" className="text-zebcast-purple hover:underline">
                privacy@zebcast.com
              </a>{' '}
              with the device identifier shown in Settings &rarr; Privacy &rarr; Your Device ID.
              We will respond within 30 days.
            </p>
            <p>
              If you are not satisfied with our response, you have the right to lodge a complaint
              with the Information Commissioner's Office (ICO):{' '}
              <a href="https://ico.org.uk/make-a-complaint/" target="_blank" rel="noopener noreferrer" className="text-zebcast-purple hover:underline">
                ico.org.uk/make-a-complaint
              </a>.
            </p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              11. Children's Privacy
            </h2>
            <p>
              ZebCast is not directed at children under the age of 13. We do not knowingly collect
              personal information from children under 13. If we become aware that we have collected
              data from a child under 13, we will take steps to delete that information promptly.
            </p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              12. Changes to This Policy
            </h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of changes
              by posting the updated policy on this page and updating the "Last updated" date at
              the top. Material changes will also be communicated through the app. Your continued
              use of ZebCast after changes are posted constitutes acceptance of the updated policy.
            </p>
          </section>

          {/* ============================== */}
          <section>
            <h2 className="text-xl font-semibold text-gray-900 mt-8 mb-3 pb-2 border-b border-gray-200">
              13. Contact Us
            </h2>
            <p>
              If you have questions or concerns about this Privacy Policy or how your data is
              handled, please contact us at:
            </p>
            <p className="mt-2 font-medium">
              Email:{' '}
              <a href="mailto:privacy@zebcast.com" className="text-zebcast-purple hover:underline">
                privacy@zebcast.com
              </a>
              <br />
              EzBox Media Limited
              <br />
              United Kingdom
            </p>
          </section>
        </div>

        <div className="mt-10 pt-6 border-t border-gray-200 flex flex-wrap justify-center gap-6 text-sm">
          <Link
            to="/"
            className="text-zebcast-purple hover:text-zebcast-dark font-medium transition-colors"
          >
            &larr; Back to Zebcast
          </Link>
          <Link
            to="/support"
            className="text-zebcast-purple hover:text-zebcast-dark font-medium transition-colors"
          >
            Support
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
